import { Form, Formik, FormikHelpers } from "formik";
import {
    ImageContentSchema,
    UpsertContentDTO,
    UpsertContentDTOSchema,
} from "src/app/types/content";
import { WritingType } from "src/app/types/orders/order";
import { ContentTransport } from "src/app/transports/content";
import { CheckTextInfo, SendTextOrderForm, SimpleModal } from "../../components";
import { toast } from "react-toastify";
import { useIntl } from "react-intl";
import { useState } from "react";
import { apiToFormikErrors } from "src/app/utils/formHelpers";
import { TDocMetrics } from "src/app/utils/textCheckHandler/handleData";
import { array, object } from "yup";
import Pacman from "src/app/components/layout/Pacman";
import { useCheckText, useGetOrderById } from "src/app/hooks";
import { IActionsModal } from "src/app/types/actionsModal";

export interface ISendTextOrderFormContainer extends IActionsModal<{ id: number }> {
    checkChanges?: boolean;
    isForReview?: boolean;
    checkMinimumWords?: boolean;
}

const SendTextOrderFormContainer = (props: ISendTextOrderFormContainer) => {
    const {
        handleClose = () => {},
        entity,
        updateGridOnSave = () => {},
        checkChanges = false,
        isForReview = false,
        checkMinimumWords = true,
    } = props;
    const [loading, setLoading] = useState(false);
    const [showCheckModal, setCheckModal] = useState(false);
    const [docMetrics, setDocMetrics] = useState<TDocMetrics>();
    const [disableConfirmationButton, setDisableConfirmationButton] = useState(true);

    const { order, loading: orderLoading } = useGetOrderById(entity?.id);

    const { checkResult, checkText, loading: checkTextLoading } = useCheckText();

    const { formatMessage } = useIntl();

    const validationType = () => {
        if (order?.writer?.hasDuplicateContentCheck && !order.deliveredAt) {
            return "COPYSCAPE";
        }

        return "NONE";
    };

    let validationSchema: any = UpsertContentDTOSchema.concat(
        object({
            images: array()
                .of(ImageContentSchema)
                .when("writingType", {
                    is: () =>
                        order?.writerNeeded &&
                        order?.content?.writingType !== WritingType.AI_MANUAL,
                    then: (schema) =>
                        schema
                            .min(1, "error.content.image.required")
                            .required("error.content.image.required"),
                    otherwise: (schema) => schema.nullable(),
                }),
        })
    );

    if (order?.content?.writingType === WritingType.AI_AUTO) {
        validationSchema = validationSchema.omit(["metaTitle", "metaDescription"]);
    }

    const handleCheckModal = (text: string) => {
        checkText(text, entity?.id);

        setCheckModal(true);
    };

    const handleDisableButton = (disable: boolean) => {
        setDisableConfirmationButton(disable);
    };
    const handleDisableCheckTextButton = (): boolean => {
        const CopyScapeValidation = !checkTextLoading && checkResult?.CopyScape?.checkPassed;

        return validationType() === "COPYSCAPE" && !CopyScapeValidation;
    };

    const handleSubmit = async (_values: UpsertContentDTO, formikHelpers: FormikHelpers<any>) => {
        setLoading(true);
        const currentValues = {
            ..._values,
            wordCount: docMetrics?.numberOfWords,
            orderId: entity?.id,
        };

        try {
            const res = await ContentTransport.saveContentInOrder(currentValues, _values.id);
            const newEntity = {
                ...order,
                content: res,
            };

            await updateGridOnSave(newEntity, false, true);
            toast.success(formatMessage({ id: "common.notification.success" }));
            handleClose();
        } catch (error: any) {
            if (error?.response?.data?.detail && error?.response?.data?.detail.includes("%")) {
                const responseStrings = error?.response?.data?.detail.split("%");
                toast.error(
                    formatMessage(
                        { id: "common.notification.error.copyscape" },
                        { percent: responseStrings[0], url: responseStrings[1] }
                    )
                );
            } else if (
                error?.response?.data?.detail &&
                error?.response?.data?.detail.includes("YTG")
            ) {
                const { YTG } = JSON.parse(error?.response?.data?.detail);

                toast.error(
                    formatMessage({ id: "common.notification.error.YTG" }, { percent: YTG })
                );
            } else {
                toast.error(
                    error?.response?.data?.detail ||
                        formatMessage({ id: "common.notification.error" })
                );
            }
            apiToFormikErrors(error?.response, formikHelpers);
        } finally {
            setLoading(false);
            CheckTextInfo;
        }
        setLoading(false);
    };

    return (
        <>
            {!order || orderLoading ? (
                <></>
            ) : (
                <Formik
                    initialValues={
                        order?.content
                            ? ContentTransport.formTransformer(order?.content)
                            : UpsertContentDTOSchema.getDefault()
                    }
                    validationSchema={validationSchema}
                    onSubmit={(values: object, formikHelpers: FormikHelpers<object>) =>
                        handleSubmit(values as UpsertContentDTO, formikHelpers)
                    }
                >
                    {(formikProps) => {
                        const text: string | unknown = formikProps?.getFieldMeta("text")?.value;
                        return (
                            <SimpleModal
                                entityName={formatMessage({ id: "common.entity.sendText" })}
                                handleClose={handleClose}
                                loading={loading}
                                buttonConfirmationAction={
                                    validationType() !== "NONE"
                                        ? () => handleCheckModal(JSON.stringify(text))
                                        : undefined
                                }
                                formId="sendTextForm"
                                disableConfirmation={disableConfirmationButton}
                                showModal={true}
                                modalProps={{ fullscreen: true }}
                            >
                                <>
                                    <Form id="sendTextForm">
                                        <SendTextOrderForm
                                            checkMinimumWords={checkMinimumWords}
                                            checkChanges={checkChanges}
                                            entity={order}
                                            isForReview={isForReview}
                                            handleDisableButton={handleDisableButton}
                                            docMetrics={docMetrics}
                                            setDocMetrics={setDocMetrics}
                                        />
                                    </Form>
                                    <SimpleModal
                                        showModal={showCheckModal}
                                        loading={loading}
                                        disableConfirmation={handleDisableCheckTextButton()}
                                        entityName={formatMessage({
                                            id: `common.entity.textCheck${
                                                validationType() !== "NONE"
                                                    ? `.${validationType()}`
                                                    : ""
                                            }`,
                                        })}
                                        handleClose={() => setCheckModal(false)}
                                        formId="sendTextForm"
                                        modalProps={{ size: "xl" }}
                                    >
                                        <>
                                            {!checkTextLoading ? (
                                                <CheckTextInfo info={checkResult} />
                                            ) : (
                                                <Pacman />
                                            )}
                                        </>
                                    </SimpleModal>
                                </>
                            </SimpleModal>
                        );
                    }}
                </Formik>
            )}{" "}
        </>
    );
};

export default SendTextOrderFormContainer;
